/**
 * This module is designed to seamlessly modify the map's view in response to location changes.
 * Such changes can occur during the initial page load or when users utilize the typeahead feature to navigate to
 * new locations. The fundamental aim of this module is to elevate user experience by maintaining an ideal zoom level
 * on the map. By doing so, users can effortlessly explore all pertinent results within their designated
 * area of interest, eliminating the need for manual adjustments.
 */

import { updateBoundaries, drawBoundary } from 'libraries/mapLibrary';
import { store$ } from '@redux/store';
import {
  distinctUntilChanged,
  filter,
  map,
  distinctUntilKeyChanged,
  withLatestFrom,
  tap,
  fromEvent,
  combineLatest,
  shareReplay,
  startWith,
  merge,
} from 'rxjs';
import messageService from 'layout/services/messageService';
import { LocationType, selectLocation } from 'features/searchResultsFacets';
import { selectLayoutBreakpoint } from 'layout/redux/selectors';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import { isSearchWithinMapEnabled, isMapLibraryLoaded } from '../redux/selectors';
import { locationSearchParameters } from '../constants/searchParametersConstants';

const message$ = messageService.getMessage();
const boundaryProperties = ['MinLatitude', 'MinLongitude', 'MaxLatitude', 'MaxLongitude'];
/**
 * This observable tells us whenever there was a custom event triggered in the document
 * indicating the presence of new boundaries shown in the map.
 */
const onNewBoundariesShown$ = fromEvent(document, 'newBoundariesShown');

/**
 * This observable tells us whenever there was a location change in the store.
 */
const newLocation$ = store$.pipe(
  filter(selectLocation),
  filter((state) => !isSearchWithinMapEnabled(state)),
  filter((state) => locationSearchParameters(state).locationType !== LocationType.RectangularArea.toString()),
  map(selectLocation),
  distinctUntilChanged(),
);

/**
 * This observable tells us whenever the map is ready to receive operations on it.
 */
const onMapLoaded$ = store$.pipe(filter(isMapLibraryLoaded), map(isMapLibraryLoaded), distinctUntilChanged());

/**
 * Observable that emits whether the current layout breakpoint is desktop (not small).
 * Emits true for desktop/tablet breakpoints and false for mobile breakpoints.
 * Uses shareReplay to multicast the value to multiple subscribers efficiently.
 * with the latest location information, facilitated by the distinctUntilKeyChanged opertor.
 * Consequently, the resulting mapped value consistently represents both the boundaries and the user's current map view.
 * This observable will emit if and only if the map is ready, that is why we have a combineLatest operator on both
 * observables: on new messages, and on map loaded.
 */
const breakpoint$ = store$.pipe(
  map(selectLayoutBreakpoint),
  filter(Boolean),
  map((breakpoint) => breakpoint.getValue() !== LayoutBreakpoint.Small.getValue()),
  distinctUntilChanged(),
  shareReplay({ bufferSize: 1, refCount: true }),
);
/**
 * Observable that tracks the user's explicit map/list view toggle state.
 * Emits true when the map view is toggled on, false when the list view is toggled on.
 * Starts with false (list view default) and uses shareReplay for efficient multicasting.
 */
const mapToggle$ = merge(
  fromEvent(document.body, 'mapViewToggled').pipe(map(() => true)),
  fromEvent(document.body, 'listViewToggled').pipe(map(() => false)),
).pipe(startWith(false), distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));

/**
 * Observable that determines if the map should be visible based on breakpoint and toggle state.
 * The map is visible when either:
 * - The viewport is desktop/tablet size (regardless of toggle state), OR
 * - The user has explicitly toggled to map view on mobile
 * Combines breakpoint, toggle state, and map loaded status to compute visibility.
 */
const mapVisible$ = combineLatest([breakpoint$, mapToggle$, onMapLoaded$]).pipe(
  map(([isDesktop, isMapToggled]) => isDesktop || isMapToggled),
  distinctUntilChanged(),
  shareReplay({ bufferSize: 1, refCount: true }),
);

/**
 * This stream combines map visibility with incoming messages to determine when to update boundaries.
 * Boundary updates only occur when the map is visible (based on breakpoint, toggle state, and map loaded status)
 * and when valid locations, boundaries and message types are present.
 */
combineLatest([mapVisible$, message$])
  .pipe(
    withLatestFrom(newLocation$),
    map(([[isMapLoaded, message], locations]) => ({
      locations,
      isMapLoaded,
      boundaries: message?.payload.mapBoundaries,
      messageType: message?.messageType,
    })),
    filter(
      ({ isMapLoaded, locations, messageType, boundaries }) =>
        isMapLoaded && Boolean(locations) && Boolean(boundaries) && Boolean(messageType),
    ),
    distinctUntilKeyChanged('locations'),
    filter((information) => information.messageType === 'mapBoundaries'),
    tap((information) => {
      drawBoundary(information.boundaries?.BoundaryUrl);
    }),
    filter((information) => information.boundaries),
    filter((information) => !information.boundaries?.BoundaryUrl),
  )
  .subscribe((information) => setTimeout(updateBoundaries.bind(this, information.boundaries), 0));

/*
  Subscribing to the store.
  When search within map is enabled, we have to clean the search boundary since results are
  going to be shown outside of it.
  The way of cleaning it is to call the function that draws boundaries with a null argument for the boundary url.
*/
store$.pipe(map(isSearchWithinMapEnabled), distinctUntilChanged()).subscribe((isSearchWithinMap) => {
  if (isSearchWithinMap) {
    drawBoundary();
  }
});

/**
 * Subscribing to the messaging service.
 * When there are new boundaries for the auto zoom fit functionality on paid markers, and also there is a
 * new boundary shown on the map that shows a visual hint for the location searched, this observable will
 * grab both pieces of information and auto fit the map zoom so the boundary and the paid markers are all
 * visible on screen.
 * This is particular useful for searches like zip codes, where the search type is not exact but radius, so
 * information appear outside of the boundary around the interested area.
 * This observable will emit if and only if the map is ready, that is why we have a combineLatest operator on both
 * observables: on new boundaries, and on map loaded.
 */
combineLatest([onNewBoundariesShown$, onMapLoaded$])
  .pipe(
    map(([newBoundary]) => newBoundary),
    withLatestFrom(message$, newLocation$),
    map(([newBoundary, newMessage, newLocation]) => {
      /*
        if there is no marker boundaries, it means either we are showing just basic results, or it could be that
        there are no results at all to show based on the user's search criteria. By rule, we don't do auto fit on
        basic results. Additionally, in cases where there are no results, there are no subjects to auto-fit on the map.
        For these scenarios, we are relying just on the location boundaries (if any) for auto fit the map zoom,
        since this is, at the end of the day, the area the user is interested in finding a home.
      */
      const areThereMarkerBoundaries =
        newMessage?.payload?.mapBoundaries &&
        boundaryProperties.every((property) => Object.hasOwn(newMessage?.payload?.mapBoundaries, property));

      const markerBoundary = areThereMarkerBoundaries
        ? newMessage?.payload?.mapBoundaries
        : newBoundary?.detail?.boundaries;

      return {
        markerBoundary,
        locationBoundary: newBoundary?.detail?.boundaries,
        locationType: newLocation?.locationType?.toLowerCase(),
      };
    }),
    filter(({ locationBoundary }) => boundaryProperties.every((property) => Object.hasOwn(locationBoundary, property))),
    map(({ locationBoundary, markerBoundary }) => ({
      MinLatitude: Math.min(locationBoundary.MinLatitude, markerBoundary.MinLatitude),
      MaxLatitude: Math.max(locationBoundary.MaxLatitude, markerBoundary.MaxLatitude),
      MinLongitude: Math.min(locationBoundary.MinLongitude, markerBoundary.MinLongitude),
      MaxLongitude: Math.max(locationBoundary.MaxLongitude, markerBoundary.MaxLongitude),
    })),
  )
  .subscribe((boundaries) => setTimeout(updateBoundaries.bind(this, boundaries), 0));
